import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import PermissionComponent from '~/components/Common/PermissionedComponent';

import {
  NDC_SUPPORTED_CARRIERS,
  useCreateBusinessNdcPreference,
  useGetBusinessNdcPreferences,
  useUpdateBusinessNdcPreference,
} from '~/hooks/apis/business-traveller/useBusinessNdcPreferences';

import { BusinessNdcPreference } from '~/services/BusinessTraveller/BusinessTravellerService';

interface Params {
  businessId: string;
}

export default function NdcFlightsTab() {
  const { businessId } = useParams<Params>();
  const { enqueueSnackbar } = useSnackbar();

  const [searchQuery, setSearchQuery] = useState('');
  const [pendingCarrier, setPendingCarrier] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{
    carrier: (typeof NDC_SUPPORTED_CARRIERS)[number];
    nextEnabled: boolean;
  } | null>(null);

  const { data: preferences = [], isLoading, error } = useGetBusinessNdcPreferences(businessId);
  const createMutation = useCreateBusinessNdcPreference(businessId);
  const updateMutation = useUpdateBusinessNdcPreference(businessId);

  const preferencesByCarrier = useMemo(() => {
    const map = new Map<string, BusinessNdcPreference>();
    for (const pref of preferences) {
      map.set(pref.carrierCode, pref);
    }
    return map;
  }, [preferences]);

  const filteredCarriers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return NDC_SUPPORTED_CARRIERS;
    }
    return NDC_SUPPORTED_CARRIERS.filter((c) => c.code.toLowerCase().includes(q) || c.label.toLowerCase().includes(q));
  }, [searchQuery]);

  async function handleConfirmToggle() {
    if (!pendingConfirm) {
      return;
    }
    const { carrier, nextEnabled } = pendingConfirm;
    const existing = preferencesByCarrier.get(carrier.code);
    setPendingCarrier(carrier.code);
    setPendingConfirm(null);
    try {
      if (existing) {
        await updateMutation.mutateAsync({ ndcPreferenceId: existing.id, enabled: nextEnabled });
      } else {
        await createMutation.mutateAsync({ carrierCode: carrier.code, enabled: nextEnabled });
      }
      enqueueSnackbar(`NDC ${nextEnabled ? 'enabled' : 'disabled'} for ${carrier.label}`, { variant: 'success' });
    } catch (e) {
      const message = e instanceof Error ? e.message : JSON.stringify(e);
      enqueueSnackbar(`Failed to update NDC preference for ${carrier.label}: ${message}`, { variant: 'error' });
    } finally {
      setPendingCarrier(null);
    }
  }

  const fallback = (
    <Stack gap={2}>
      <Typography variant="h4">Permission required</Typography>
      <Typography variant="body2">You are not authorized to view this page</Typography>
    </Stack>
  );

  return (
    <PermissionComponent fallback={fallback}>
      <Stack gap={2} maxWidth={1000}>
        <Alert severity="warning">This feature is still under implementation and is not live in production yet.</Alert>

        <Typography variant="body2" color="text.secondary">
          Turn on NDC flights per airline for this business. When NDC is on, Edifact is off for that airline.
        </Typography>

        <TextField
          label="Search airline"
          placeholder="Search by airline name or code"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
        />

        {isLoading && <CircularProgress size={24} />}

        {error && (
          <Alert severity="error">
            Failed to load NDC preferences: {error instanceof Error ? error.message : 'Unknown error'}
          </Alert>
        )}

        {!isLoading && !error && (
          <List disablePadding>
            {filteredCarriers.length === 0 && (
              <ListItem>
                <ListItemText primary="No airlines match your search." />
              </ListItem>
            )}
            {filteredCarriers.map((carrier, index) => {
              const existing = preferencesByCarrier.get(carrier.code);
              const enabled = existing?.enabled ?? false;
              const isPending = pendingCarrier === carrier.code;

              return (
                <React.Fragment key={carrier.code}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem
                    secondaryAction={
                      <Stack direction="row" alignItems="center" gap={1}>
                        {isPending && <CircularProgress size={16} />}
                        <Switch
                          checked={enabled}
                          disabled={isPending}
                          onChange={(_, checked) => setPendingConfirm({ carrier, nextEnabled: checked })}
                          inputProps={{ 'aria-label': `Toggle NDC flights for ${carrier.label}` }}
                        />
                      </Stack>
                    }
                  >
                    <ListItemText
                      primary={`${carrier.label}: turn on NDC flights`}
                      secondary={`Carrier code: ${carrier.code}`}
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Stack>

      <Dialog open={!!pendingConfirm} onClose={() => setPendingConfirm(null)}>
        <DialogTitle>
          {pendingConfirm?.nextEnabled ? 'Turn on' : 'Turn off'} NDC flights for {pendingConfirm?.carrier.label}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingConfirm?.nextEnabled
              ? `Travellers at this business will start seeing NDC fares for ${pendingConfirm?.carrier.label} on their next search. This change takes effect immediately.`
              : `Travellers at this business will start seeing Edifact fares for ${pendingConfirm?.carrier.label} on their next search. This change takes effect immediately.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingConfirm(null)}>Cancel</Button>
          <Button onClick={handleConfirmToggle} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </PermissionComponent>
  );
}
